/**
 * CareersPage — premium public recruiting page (v2-quality redesign).
 *
 * Mirrors the v2 landing system (HowItWorks / PillarsBento): SectionShell rhythm,
 * shared Reveal/Stagger/GradientMesh/CountUp/GlassCard primitives, single indigo
 * accent + emerald trust dot, logical props, h2.display.serif-jp, JP-B2B 安心感
 * tone with restrained motion. All chrome (SiteHeader/SiteFooter) is rendered
 * here because this is a full marketing page, not a LegalShell prose document.
 *
 * Structure:
 *   1. Hero band — why work at keenez (GradientMesh + Reveal), trust chips.
 *   2. Values band — remote-friendly, craft, bilingual JP/EN, ownership (cards).
 *   3. "How we work" strip — honest CountUp stats + working-principle chips.
 *   4. Open roles — extended OPENINGS array as polished hover cards → mailto.
 *   5. Closing CTA — "Don't see your role? Tell us." → /contact (dark banner).
 *
 * Existing `careers.*` i18n keys are preserved and reused; all NEW copy uses
 * t('careers.xxx', { defaultValue: 'English' }) so it renders before locale keys
 * land while respecting any future override.
 */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  MapPin,
  Briefcase,
  Globe2,
  Sparkles,
  Hammer,
  Languages,
  Compass,
  ShieldCheck,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import {
  Reveal,
  Stagger,
  SectionShell,
  GradientMesh,
  GlassCard,
  CountUp,
  fadeInUp,
  SPRING_SOFT,
  usePrefersReducedMotion,
} from '@/components/landing/v2';

const CAREERS_EMAIL = 'careers@keenez.com';

// ─────────────────────────────────────────────────────────────────────────────
// Open roles. Existing four keys are preserved; two realistic roles added
// (Founding GTM — Japan, Payroll compliance specialist — JP). New roles carry
// their own defaultValue copy alongside the key so they render immediately.
// ─────────────────────────────────────────────────────────────────────────────

interface JobPosting {
  id: string;
  titleKey: string;
  /** English fallback when the locale key is absent (new roles only). */
  titleDefault?: string;
  locationKey: string;
  locationDefault?: string;
  teamKey: string;
  teamDefault?: string;
  /** Employment type chip — defaulted copy only (no existing keys). */
  typeKey: string;
  typeDefault: string;
}

const OPENINGS: JobPosting[] = [
  {
    id: 'eng-payroll',
    titleKey: 'careers.jobs.eng_payroll.title',
    locationKey: 'careers.location.tokyo_remote',
    teamKey: 'careers.teams.engineering',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
  {
    id: 'eng-ai',
    titleKey: 'careers.jobs.eng_ai.title',
    locationKey: 'careers.location.remote_global',
    teamKey: 'careers.teams.engineering',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
  {
    id: 'gtm-jp',
    titleKey: 'careers.jobs.gtm_jp.title',
    titleDefault: 'Founding GTM — Japan',
    locationKey: 'careers.location.tokyo',
    teamKey: 'careers.teams.gtm',
    teamDefault: 'Go-to-market',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
  {
    id: 'payroll-compliance-jp',
    titleKey: 'careers.jobs.payroll_compliance_jp.title',
    titleDefault: 'Payroll compliance specialist (JP)',
    locationKey: 'careers.location.tokyo_remote',
    teamKey: 'careers.teams.compliance',
    teamDefault: 'Compliance',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
  {
    id: 'design',
    titleKey: 'careers.jobs.design.title',
    locationKey: 'careers.location.tokyo_remote',
    teamKey: 'careers.teams.design',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
  {
    id: 'cs-jp',
    titleKey: 'careers.jobs.cs_jp.title',
    locationKey: 'careers.location.tokyo',
    teamKey: 'careers.teams.customer',
    typeKey: 'careers.type.full_time',
    typeDefault: 'Full-time',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Culture / values. Single indigo accent ramp; honest, restrained copy.
// ─────────────────────────────────────────────────────────────────────────────

interface Value {
  id: string;
  Icon: LucideIcon;
  titleKey: string;
  titleDefault: string;
  bodyKey: string;
  bodyDefault: string;
  accent: string;
}

const VALUES: Value[] = [
  {
    id: 'remote',
    Icon: Globe2,
    titleKey: 'careers.values.remote.title',
    titleDefault: 'Distributed, async-friendly',
    bodyKey: 'careers.values.remote.body',
    bodyDefault:
      'Tokyo, Berlin, SF. We write things down, default to async, and meet with intent — not by calendar gravity.',
    accent: 'from-sky-500 to-indigo-500',
  },
  {
    id: 'craft',
    Icon: Hammer,
    titleKey: 'careers.values.craft.title',
    titleDefault: 'Craft over volume',
    bodyKey: 'careers.values.craft.body',
    bodyDefault:
      'Payroll is unforgiving — a yen off is a bug. We ship slowly enough to be exactly right, and fast enough to matter.',
    accent: 'from-indigo-500 to-fuchsia-500',
  },
  {
    id: 'bilingual',
    Icon: Languages,
    titleKey: 'careers.values.bilingual.title',
    titleDefault: 'Bilingual by design',
    bodyKey: 'careers.values.bilingual.body',
    bodyDefault:
      'JA and EN are first-class — in the product, in our docs, and in the room. Statutory nuance survives translation here.',
    accent: 'from-violet-500 to-indigo-500',
  },
  {
    id: 'ownership',
    Icon: Compass,
    titleKey: 'careers.values.ownership.title',
    titleDefault: 'Ownership, equity for everyone',
    bodyKey: 'careers.values.ownership.body',
    bodyDefault:
      'Small team, real scope. You own outcomes end-to-end, hold meaningful equity, and your judgment is trusted.',
    accent: 'from-emerald-500 to-sky-500',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// How-we-work stats. Honest and modest — a seed-stage team, not inflated.
// CountUp gates itself under reduced-motion (renders final value).
// ─────────────────────────────────────────────────────────────────────────────

interface Stat {
  id: string;
  to: number;
  suffix?: string;
  labelKey: string;
  labelDefault: string;
}

const STATS: Stat[] = [
  { id: 'timezones', to: 3, labelKey: 'careers.stats.timezones', labelDefault: 'Time zones, one team' },
  { id: 'jurisdictions', to: 4, labelKey: 'careers.stats.jurisdictions', labelDefault: 'Live payroll jurisdictions' },
  { id: 'overlap', to: 4, suffix: 'h', labelKey: 'careers.stats.overlap', labelDefault: 'Core overlap hours / day' },
  { id: 'equity', to: 100, suffix: '%', labelKey: 'careers.stats.equity', labelDefault: 'Of the team holds equity' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────

function CareersHero() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  const chips = [
    { id: 'tokyo', label: 'Tokyo' },
    { id: 'berlin', label: 'Berlin' },
    { id: 'sf', label: 'SF' },
  ];

  return (
    <section className="relative isolate overflow-hidden pt-16 pb-12 md:pt-24 md:pb-16">
      <GradientMesh />

      <div className="container relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Stagger className="max-w-3xl">
          <Reveal as="p">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary backdrop-blur-sm">
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t('careers.eyebrow')}
            </span>
          </Reveal>

          <Reveal as="div" delay={0.06}>
            <h1 className="display serif-jp mt-5 text-balance text-4xl font-semibold leading-[1.12] tracking-tight md:text-6xl">
              {t('careers.hero.title', {
                defaultValue: 'Build the HR platform that finally serves both sides.',
              })}
            </h1>
          </Reveal>

          <Reveal as="p" delay={0.12}>
            <span className="mt-5 block max-w-2xl text-base leading-relaxed text-muted-foreground text-pretty md:text-lg">
              {t('careers.hero.subtitle', {
                defaultValue:
                  'keenez runs Japan-native payroll and global payroll in one product — AI-native from day one. We are a small, deliberate team building the system cross-border companies have been faking with spreadsheets and bilingual contractors.',
              })}
            </span>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <motion.div whileHover={reduced ? undefined : { y: -2 }} transition={SPRING_SOFT}>
                <a
                  href="#openings"
                  className="gradient-primary inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 text-sm font-semibold text-white shadow-glow transition-shadow duration-200 hover:shadow-glow-pink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {t('careers.hero.cta', { defaultValue: 'See open roles' })}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                </a>
              </motion.div>

              <ul className="flex flex-wrap items-center gap-2">
                {chips.map((c) => (
                  <li
                    key={c.id}
                    className="num inline-flex items-center gap-1.5 rounded-lg border border-border bg-background/60 px-2.5 py-1 text-xs font-medium text-foreground/80"
                  >
                    <MapPin aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Stagger>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Why keenez — reuses existing careers.why.* prose keys, premium framing.
// ─────────────────────────────────────────────────────────────────────────────

function WhyKeenez() {
  const { t } = useTranslation();

  return (
    <SectionShell
      id="why"
      tone="muted"
      width="6xl"
      eyebrow={t('careers.why.eyebrow', { defaultValue: 'Why us' })}
      heading={t('careers.why.title')}
      subhead={t('careers.why.p2')}
      background={<GradientMesh blobs={false} />}
    >
      <Reveal variants={fadeInUp}>
        <GlassCard className="p-6 md:p-8">
          <p className="text-base leading-relaxed text-foreground/80 text-pretty md:text-lg">
            {t('careers.why.p1')}
          </p>
        </GlassCard>
      </Reveal>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Values band
// ─────────────────────────────────────────────────────────────────────────────

function ValuesBand() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  return (
    <SectionShell
      id="values"
      eyebrow={t('careers.values.eyebrow', { defaultValue: 'How we operate' })}
      heading={t('careers.values.title', { defaultValue: 'A few things we actually mean.' })}
      subhead={t('careers.values.sub', {
        defaultValue:
          'Not a poster. These are the trade-offs we make every week — and how we expect to work with you.',
      })}
      background={<GradientMesh blobs />}
    >
      <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
        {VALUES.map((v) => {
          const Icon = v.Icon;
          return (
            <Reveal key={v.id} variants={fadeInUp} as="article">
              <motion.div
                className="h-full"
                whileHover={reduced ? undefined : { y: -3 }}
                transition={SPRING_SOFT}
              >
                <div className="group/value relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-soft transition-shadow duration-200 hover:shadow-card-lift">
                  <div
                    aria-hidden
                    className={`pointer-events-none absolute -top-16 -end-16 h-40 w-40 rounded-full bg-gradient-to-br ${v.accent} opacity-10 blur-2xl transition-opacity duration-500 group-hover/value:opacity-25`}
                  />
                  <div className="relative">
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${v.accent} text-white shadow-glow`}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold tracking-tight text-foreground md:text-xl">
                      {t(v.titleKey, { defaultValue: v.titleDefault })}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground text-pretty">
                      {t(v.bodyKey, { defaultValue: v.bodyDefault })}
                    </p>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          );
        })}
      </Stagger>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// How-we-work stats strip
// ─────────────────────────────────────────────────────────────────────────────

function HowWeWork() {
  const { t } = useTranslation();

  const principles = [
    { id: 'overlap', Icon: Clock, label: t('careers.work.overlap', { defaultValue: '±8h overlap, no 3am calls' }) },
    { id: 'equity', Icon: ShieldCheck, label: t('careers.work.equity', { defaultValue: 'Above-market pay for our stage' }) },
    { id: 'craft', Icon: Sparkles, label: t('careers.work.craft', { defaultValue: 'Real focus on craft, not theatre' }) },
  ];

  return (
    <SectionShell
      id="how-we-work"
      tone="muted"
      align="center"
      width="6xl"
      eyebrow={t('careers.work.eyebrow', { defaultValue: 'Life at keenez' })}
      heading={t('careers.work.title', { defaultValue: 'Small team, honest numbers.' })}
      subhead={t('careers.work.sub', {
        defaultValue:
          'We are early-stage and we keep these claims modest — they are true today, not aspirational.',
      })}
      background={<GradientMesh blobs={false} />}
    >
      <Stagger className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
        {STATS.map((s) => (
          <Reveal key={s.id} variants={fadeInUp} as="div">
            <GlassCard className="flex h-full flex-col items-center justify-center p-5 text-center md:p-6">
              <CountUp
                to={s.to}
                suffix={s.suffix}
                className="text-3xl font-semibold gradient-text md:text-4xl"
              />
              <p className="mt-2 text-xs leading-snug text-muted-foreground text-pretty md:text-sm">
                {t(s.labelKey, { defaultValue: s.labelDefault })}
              </p>
            </GlassCard>
          </Reveal>
        ))}
      </Stagger>

      <Reveal variants={fadeInUp} className="mt-8">
        <ul className="flex flex-col flex-wrap items-stretch justify-center gap-2.5 sm:flex-row">
          {principles.map((p) => {
            const Icon = p.Icon;
            return (
              <li
                key={p.id}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card/70 px-4 py-2.5 text-sm text-foreground/80 backdrop-blur-sm"
              >
                <Icon aria-hidden className="h-4 w-4 shrink-0 text-primary" />
                {p.label}
              </li>
            );
          })}
        </ul>
      </Reveal>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Open roles
// ─────────────────────────────────────────────────────────────────────────────

function OpenRoles() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  return (
    <SectionShell
      id="openings"
      width="6xl"
      eyebrow={t('careers.openings.eyebrow', { defaultValue: 'Open roles' })}
      heading={t('careers.openings.title')}
      subhead={t('careers.openings.sub', {
        defaultValue: 'Every role is bilingual-friendly and ships real product. Apply and we read every note.',
      })}
      background={<GradientMesh blobs />}
    >
      <Stagger as="ul" className="grid grid-cols-1 gap-4">
        {OPENINGS.map((job) => {
          const title = job.titleDefault
            ? t(job.titleKey, { defaultValue: job.titleDefault })
            : t(job.titleKey);
          const location = job.locationDefault
            ? t(job.locationKey, { defaultValue: job.locationDefault })
            : t(job.locationKey);
          const team = job.teamDefault
            ? t(job.teamKey, { defaultValue: job.teamDefault })
            : t(job.teamKey);
          const type = t(job.typeKey, { defaultValue: job.typeDefault });

          return (
            <Reveal key={job.id} variants={fadeInUp} as="li">
              <motion.div
                whileHover={reduced ? undefined : { y: -2 }}
                transition={SPRING_SOFT}
              >
                <a
                  href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(title)}`}
                  className="group flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft transition-shadow duration-200 hover:border-primary/30 hover:shadow-card-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:flex-row sm:items-center sm:justify-between sm:gap-6 md:p-6"
                >
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold tracking-tight text-foreground md:text-lg">
                      {title}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase aria-hidden className="h-3.5 w-3.5" />
                        {team}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin aria-hidden className="h-3.5 w-3.5" />
                        {location}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock aria-hidden className="h-3.5 w-3.5" />
                        {type}
                      </span>
                    </div>
                  </div>

                  <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-xl border border-border bg-background/70 px-4 py-2 text-sm font-semibold text-primary transition-colors group-hover:border-primary/40 group-hover:bg-primary/5 sm:self-auto">
                    {t('careers.apply')}
                    <ArrowRight aria-hidden className="h-4 w-4 rtl:rotate-180" />
                  </span>
                </a>
              </motion.div>
            </Reveal>
          );
        })}
      </Stagger>
    </SectionShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Closing CTA — "Don't see your role? Tell us." → /contact (dark banner).
// Mirrors CtaSection's inverted dark shell + dual paths.
// ─────────────────────────────────────────────────────────────────────────────

function ClosingCta() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  return (
    <section
      id="careers-cta"
      aria-labelledby="careers-cta-heading"
      className="relative isolate overflow-hidden py-20 md:py-28"
    >
      <div className="container relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative isolate overflow-hidden rounded-[2rem] border border-white/10 bg-ink-950 px-6 py-16 shadow-card-lift sm:px-12 md:py-20 lg:px-16">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-[0.12]" />
            <div className="absolute -start-24 -top-24 h-72 w-72 rounded-full bg-accent-500/30 blur-3xl" />
            <div className="absolute -end-20 -bottom-24 h-80 w-80 rounded-full bg-fuchsia-500/20 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_-10%,transparent_40%,rgba(6,8,12,0.55)_100%)]" />
          </div>

          <Stagger className="relative mx-auto max-w-2xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-200 backdrop-blur-sm">
                <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t('careers.general.eyebrow', { defaultValue: 'Open application' })}
              </span>
            </Reveal>

            <Reveal delay={0.06}>
              <h2
                id="careers-cta-heading"
                className="display serif-jp mt-6 text-balance text-3xl font-semibold leading-[1.18] tracking-tight text-white sm:text-4xl md:text-5xl"
              >
                {t('careers.general.title')}
              </h2>
            </Reveal>

            <Reveal delay={0.12} as="p">
              <span className="mx-auto mt-5 block max-w-xl text-base leading-[1.8] text-indigo-100/80 md:text-lg">
                {t('careers.general.p1')}
              </span>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <motion.div
                  whileHover={reduced ? undefined : { y: -2 }}
                  transition={SPRING_SOFT}
                  className="w-full sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="gradient-primary inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 text-sm font-semibold text-white shadow-glow transition-shadow duration-200 hover:shadow-glow-pink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-auto"
                  >
                    {t('careers.general.cta', { defaultValue: 'Tell us about you' })}
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                  </Link>
                </motion.div>

                <motion.div
                  whileHover={reduced ? undefined : { y: -2 }}
                  transition={SPRING_SOFT}
                  className="w-full sm:w-auto"
                >
                  <a
                    href={`mailto:${CAREERS_EMAIL}`}
                    className="num inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-auto"
                  >
                    {CAREERS_EMAIL}
                  </a>
                </motion.div>
              </div>
            </Reveal>
          </Stagger>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export function CareersPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        <CareersHero />
        <WhyKeenez />
        <ValuesBand />
        <HowWeWork />
        <OpenRoles />
        <ClosingCta />
      </main>

      <SiteFooter />
    </div>
  );
}
